import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle, Check, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/shared/Button/Button';
import FormField from '../../components/shared/FormField/FormField';
import Input from '../../components/shared/Input/Input';

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const success = await login(username, password);
    if (success) {
      navigate('/admin');
    } else {
      setError('Invalid credentials. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="flex min-h-screen flex-col bg-white lg:flex-row">
      {/* Showcase panel — burgundy */}
      <aside className="relative flex flex-col justify-between overflow-hidden bg-brand-900 px-6 py-8 text-white lg:w-1/2 lg:px-12 lg:py-12">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_100%_at_0%_0%,_theme(colors.brand.700),_transparent_55%)]"
        />
        <div
          aria-hidden
          className="texture-grain pointer-events-none absolute inset-0 opacity-[0.2]"
        />
        <div className="relative">
          <Link
            to="/"
            className="inline-flex rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-4 focus-visible:ring-offset-brand-900"
          >
            {/* Slanted wordmark, as painted on the shuttles */}
            <span className="-skew-x-6 bg-livery-600 px-3 py-1.5 font-signage text-[1.75rem] font-extrabold italic uppercase leading-none tracking-wide text-white">
              Cruiser
            </span>
          </Link>
        </div>

        <div className="relative hidden max-w-md lg:block">
          <h2 className="font-display text-[2.1rem] font-medium leading-[1.12] tracking-tight">
            Move people and parcels across every route, all in one place.
          </h2>
          <ul className="mt-8 space-y-4 text-base text-brand-100">
            {[
              'Live trip and parcel tracking',
              'Manifests, expenses & finance in one view',
              'Booking made simple for every route',
            ].map((item) => (
              <li key={item} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-700/60 text-white">
                  <Check className="h-3.5 w-3.5" aria-hidden />
                </span>
                {item}
              </li>
            ))}
          </ul>
        </div>

        <p className="relative hidden text-sm text-brand-200/80 lg:block">
          © {new Date().getFullYear()} Cruiser
        </p>
      </aside>

      {/* Form panel */}
      <main className="relative flex flex-1 items-center justify-center px-6 py-10 sm:px-10">
        <Link
          to="/"
          className="absolute left-6 top-6 inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 sm:left-10 sm:top-10"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden /> Back to home
        </Link>

        <div className="w-full max-w-md">
          <h1 className="font-display text-[2.25rem] font-medium leading-tight tracking-tight text-slate-900">
            Welcome back
          </h1>
          <p className="mt-2 text-base text-slate-600">
            Sign in to manage your fleet.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-5">
            {error && (
              <div className="flex items-start gap-2.5 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-800">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <span>{error}</span>
              </div>
            )}

            <FormField label="Username" name="username" required>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                required
                autoFocus
              />
            </FormField>

            <FormField label="Password" name="password" required>
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                trailingIcon={
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="pointer-events-auto rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                }
              />
            </FormField>

            <Button type="submit" loading={loading} fullWidth>
              {loading ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>

          <div className="mt-6 space-y-2 text-center text-sm text-slate-600">
            <p>
              Don't have an account?{' '}
              <Link
                to="/register"
                className="font-semibold text-brand-700 hover:underline"
              >
                Register
              </Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
